/**
 * Weekly-update agent — proposes the governed weekly customer update email.
 *
 * Follows the real-work producer pattern (followUpAgent / revenueAgent):
 * for each ACTIVE customer it composes the update strictly from real
 * workspace data (src/lib/cs/weeklyUpdate.ts) and, when there is genuinely
 * something to report, queues a `send_customer_update` proposal into the
 * approvals inbox. It performs NO send itself — the operator approves in
 * the inbox, and only then does the executor write the outbox doc. Honest
 * end-to-end:
 *
 *   real data → composed email → operator approval → outbox write
 *
 * Idempotency + cadence:
 *   - a customer with a pending `send_customer_update` proposal is skipped
 *   - a customer whose `lastUpdateSentAt` is within the last 6 days is
 *     skipped (the executor stamps that field on approve)
 *   - a customer with nothing real to report is skipped — no filler emails
 *   - a customer with no email on file surfaces as a "needs email" finding
 *     in the result — never a silent drop, never a guessed address
 */

import { queueApproval, listApprovals } from './approvals';
import {
  composeWeeklyUpdate,
  resolveCustomerUpdateRecipient,
  type ComposedWeeklyUpdate,
  type WeeklyUpdateData,
} from '../cs/weeklyUpdate';
import type { Person } from '../db/types';
import type { SendCustomerUpdateAction } from './types';

const DAY_MS = 86_400_000;
/** Don't re-propose within this many days of the last sent/queued update. */
export const UPDATE_CADENCE_DAYS = 6;

/** An active customer we cannot email — surfaced, never silently dropped. */
export interface NeedsEmailFinding {
  personId: string;
  personName: string;
  reason: string;
}

export interface ProposeWeeklyUpdatesResult {
  /** New `send_customer_update` proposals queued for approval. */
  proposed: number;
  /** Skipped: already pending in the inbox, or sent within the cadence. */
  skipped: number;
  /** Skipped honestly: the composer returned null (no real data). */
  nothingToReport: number;
  /** Composable updates with no email on file — needs a human to add one. */
  needsEmail: NeedsEmailFinding[];
}

/**
 * Build the exact action the agent would queue — exported so the
 * CustomerSuccessView "Queue for approval" button creates an identical
 * proposal (one composer, one action shape, one approval gate).
 */
export function buildSendCustomerUpdateAction(
  customer: Person,
  composed: ComposedWeeklyUpdate,
  to: string,
): SendCustomerUpdateAction {
  return {
    kind: 'send_customer_update',
    personId: customer.id,
    personName: customer.name,
    to,
    subject: composed.subject,
    text: composed.text,
    html: composed.html,
  };
}

/** Was a weekly update sent (outbox-queued) within the cadence window? */
function sentRecently(customer: Person, now: number): boolean {
  if (!customer.lastUpdateSentAt) return false;
  const t = Date.parse(customer.lastUpdateSentAt);
  if (Number.isNaN(t)) return false;
  return now - t < UPDATE_CADENCE_DAYS * DAY_MS;
}

/**
 * Compose + propose weekly updates for every active customer. De-dupes
 * against pending proposals and the last-sent stamp, so re-running the
 * agent never piles duplicates. Returns an honest tally.
 */
export async function proposeWeeklyUpdates(
  people: Person[],
  data: WeeklyUpdateData,
  now: number = Date.now(),
): Promise<ProposeWeeklyUpdatesResult> {
  const active = people.filter(
    (p) => p.category === 'customer' && (p.status ?? 'active') === 'active',
  );

  // Idempotency: one pending weekly-update proposal per customer, max.
  const pending = await listApprovals('pending');
  const alreadyQueued = new Set(
    pending
      .filter((a) => a.action.kind === 'send_customer_update')
      .map((a) => (a.action as SendCustomerUpdateAction).personId),
  );

  const result: ProposeWeeklyUpdatesResult = {
    proposed: 0,
    skipped: 0,
    nothingToReport: 0,
    needsEmail: [],
  };

  for (const customer of active) {
    if (alreadyQueued.has(customer.id) || sentRecently(customer, now)) {
      result.skipped++;
      continue;
    }
    const composed = composeWeeklyUpdate(customer, data, now);
    if (!composed) {
      // Nothing real happened — no filler email, no proposal.
      result.nothingToReport++;
      continue;
    }
    const recipient = resolveCustomerUpdateRecipient(customer);
    if (!recipient) {
      result.needsEmail.push({
        personId: customer.id,
        personName: customer.name,
        reason: 'No contact email on file — add a POC email to send weekly updates.',
      });
      continue;
    }
    const action = buildSendCustomerUpdateAction(customer, composed, recipient.email);
    await queueApproval(
      action,
      'scheduler',
      `Weekly update to ${recipient.email} — composed from this week's real workspace data.`,
    );
    alreadyQueued.add(customer.id);
    result.proposed++;
  }

  return result;
}
